import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type {
  ConversaCompleta,
  ConversaResumo,
  DocumentoDoContrato,
  ResultadoDaPergunta,
  SituacaoDaConsulta,
  TipoDeDocumento,
} from '../../types/documentos'

/** As chaves de cache dos documentos e da consulta — as duas pontas usam estas funções. */
export const chavesDeDocumentos = {
  lista: () => ['documentos'] as const,
  consulta: () => ['consulta'] as const,
  situacao: () => ['consulta', 'situacao'] as const,
  conversas: () => ['consulta', 'conversas'] as const,
  conversa: (id: number) => ['consulta', 'conversas', id] as const,
}

/**
 * Envio e resposta da IA esperam mais que os 30 s padrão do cliente.
 *
 * O envio sobe até 25 MB e lê um PDF de 200 páginas antes de responder; a pergunta espera o modelo,
 * que o servidor corta em 90 s. Abaixo disso, o navegador desistiria de uma resposta que chegaria.
 */
const TEMPO_DO_ENVIO_MS = 180_000
const TEMPO_DA_PERGUNTA_MS = 120_000

export function useDocumentos() {
  return useQuery({
    queryKey: chavesDeDocumentos.lista(),
    queryFn: async () => (await api.get<DocumentoDoContrato[]>('/documentos')).data,
  })
}

export interface EnvioDeDocumento {
  arquivo: File
  titulo: string
  tipo: TipoDeDocumento
}

export function useEnviarDocumento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ arquivo, titulo, tipo }: EnvioDeDocumento) => {
      const corpo = new FormData()
      corpo.append('arquivo', arquivo)
      corpo.append('titulo', titulo)
      corpo.append('tipo', tipo)
      return (await api.post<DocumentoDoContrato>('/documentos', corpo, { timeout: TEMPO_DO_ENVIO_MS })).data
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeDocumentos.lista() })
      void cliente.invalidateQueries({ queryKey: chavesDeDocumentos.situacao() })
    },
  })
}

export function useRetirarDocumento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) =>
      (await api.post<DocumentoDoContrato>(`/documentos/${id}/retirada`, { motivo })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeDocumentos.lista() })
      void cliente.invalidateQueries({ queryKey: chavesDeDocumentos.situacao() })
    },
  })
}

/** Se dá para perguntar agora. Não chama o modelo. */
export function useSituacaoDaConsulta() {
  return useQuery({
    queryKey: chavesDeDocumentos.situacao(),
    queryFn: async () => (await api.get<SituacaoDaConsulta>('/consulta')).data,
  })
}

export function useConversas() {
  return useQuery({
    queryKey: chavesDeDocumentos.conversas(),
    queryFn: async () => (await api.get<ConversaResumo[]>('/consulta/conversas')).data,
  })
}

async function buscarConversa(id: number) {
  return (await api.get<ConversaCompleta>(`/consulta/conversas/${id}`)).data
}

/** @param id a conversa aberta; nulo numa conversa nova, que ainda não existe no servidor */
export function useConversa(id: number | null) {
  return useQuery({
    queryKey: chavesDeDocumentos.conversa(id ?? 0),
    queryFn: () => buscarConversa(id ?? 0),
    enabled: id !== null,
  })
}

export function usePerguntar() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ conversaId, pergunta }: { conversaId: number | null; pergunta: string }) =>
      (
        await api.post<ResultadoDaPergunta>(
          '/consulta/perguntas',
          { conversaId: conversaId ?? undefined, pergunta },
          { timeout: TEMPO_DA_PERGUNTA_MS },
        )
      ).data,
    onSuccess: async (resultado) => {
      // A conversa é buscada ANTES de a pergunta deixar de estar "em andamento": sem isso, entre o
      // fim do envio e a chegada do fio atualizado, a pergunta sumiria da tela por um instante — e,
      // numa conversa nova, o esqueleto de carregamento piscaria no lugar dela.
      await cliente.fetchQuery({
        queryKey: chavesDeDocumentos.conversa(resultado.conversaId),
        queryFn: () => buscarConversa(resultado.conversaId),
      })
      void cliente.invalidateQueries({ queryKey: chavesDeDocumentos.conversas() })
      // O limite do mês pode ter sido atingido por esta pergunta.
      void cliente.invalidateQueries({ queryKey: chavesDeDocumentos.situacao() })
    },
  })
}

/**
 * Abre o arquivo numa aba nova, já na página citada.
 *
 * A rota exige o cabeçalho de autorização, que um link comum não manda — por isso o arquivo vem
 * pelo cliente HTTP e abre como `blob:`. A aba é aberta ANTES de esperar o download: aberta depois
 * de um `await`, o bloqueador de pop-up do navegador a barraria como janela não pedida.
 *
 * @param documentoId o documento
 * @param pagina a página citada; o visualizador de PDF do navegador entende `#page=N`
 */
export async function abrirDocumento(documentoId: number, pagina?: number) {
  const aba = window.open('', '_blank')
  try {
    const resposta = await api.get(`/documentos/${documentoId}/conteudo`, { responseType: 'blob' })
    const endereco = URL.createObjectURL(resposta.data as Blob)
    const destino = pagina ? `${endereco}#page=${pagina}` : endereco
    if (aba) {
      aba.location.href = destino
    } else {
      window.location.assign(destino)
    }
    // A aba nova precisa de tempo para ler o blob antes de ele ser liberado da memória.
    window.setTimeout(() => URL.revokeObjectURL(endereco), 60_000)
  } catch (erro) {
    aba?.close()
    throw erro
  }
}
